import { Box, Button, CircularProgress, FormControlLabel, Popover, Stack, Switch, TextField, Tooltip, Typography } from "@mui/material";
import Container from "@mui/material/Container";
import * as React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CustomSentenceApi, ICustomSentence } from "../api/CustomSentenceApi";
import { LS_PUBLIC_SCORE_IMPORT_ID } from "../referrerStorage";

import { AudioApi } from "../api/AudioApi";
import { ChineseTeacher } from "../api/UserApi";
import SidebarCollapseIcon from "../asset/icon-sidebar-collapse.png";
import SidebarExpandIcon from "../asset/icon-sidebar-expand.png";
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { handleErrorMessage } from '../component/ErrorDialog';
import AppBarContext from '../context/AppBarContext';
import { useAudioPlayer } from "../context/AudioPlayerContext";
import CustomSentenceListContext from "../context/CustomSentenceListContext";
import { useKaiTiFontStyles } from "../font/KaiTiFont";
import { useSentenceChinesePinyin } from "../hook/useSentenceChinesePinyin";
import CustomSentenceRecorderControl, { CustomSentenceUseRecorder, useRecorder } from "./CustomSentenceRecorderControl";
import CustomSentenceRecordingList from "./CustomSentenceRecordingList";
import SendButtonTextField from "./SendButtonTextField";
import { TeacherAudioActions, getSelectedTeacherSpeakerRole } from "./TeacherAudioReportButton";
import { BLUE, INK, NAVY, SLATE, SOFT_BLUE, YELLOW, YELLOW_HOVER } from "../style/verbaScholaPalette";

function getInlineErrorMessage(err: any): string {
    const data = err?.response?.data;
    if (typeof data === "string") return data;
    if (typeof data?.message === "string") return data.message;
    if (typeof err?.message === "string") return err.message;
    return "An unexpected error occurred.";
}

function selectedVoiceIsPlayable(sentence: ICustomSentence, teacher: ChineseTeacher): boolean {
    if (sentence.playable_teacher_voices !== undefined) {
        return sentence.playable_teacher_voices.includes(teacher);
    }
    return !sentence.processing_error && (sentence.processing_queue ?? 0) <= 0;
}

export default function CustomSentenceInterface({ isMenuCollapsed = false, onToggleMenuCollapse = () => { }, isMobile = false, alwaysShowPinyin = false, redesigned = false }: { isMenuCollapsed?: boolean; onToggleMenuCollapse?: () => void; isMobile?: boolean; alwaysShowPinyin?: boolean; redesigned?: boolean }) {
    const { customSentenceList, setCustomSentenceList, selectedSentence, setSelectedSentence, refreshCustomSentenceList } = React.useContext(CustomSentenceListContext);
    const { setErrorMessage } = React.useContext(AppBarContext);

    const history = useHistory();
    const location = useLocation();

    const { playAudio } = useAudioPlayer();
    const { recorderState, ...handlers }: CustomSentenceUseRecorder = useRecorder(setErrorMessage);
    const { audio } = recorderState;

    const kaiTiFontClass = useKaiTiFontStyles();

    const [publicSentenceScoreDone, setPublicSentenceScoreDone] = React.useState(false);
    const [addErrorMessage, setAddErrorMessage] = React.useState<string | null>(null);
    const [isSubmitting, setSubmitting] = React.useState(false);
    const [teacher, setTeacher] = React.useState(() => AudioApi.selectedCustomSentenceTeacher());
    const [audioRequestError, setAudioRequestError] = React.useState<string | null>(null);
    const [audioRequestRetry, setAudioRequestRetry] = React.useState(0);
    const [playbackSettingsRefreshing, setPlaybackSettingsRefreshing] = React.useState(false);
    const requestedAudio = React.useRef(new Set<string>());
    const audioRequestsInFlight = React.useRef(new Set<string>());
    const explicitAudioRetries = React.useRef(new Set<string>());
    const requestAfterSettingsChange = React.useRef<string | null>(null);
    const playbackRefreshVersion = React.useRef(0);
    const selectedSentenceId = React.useRef(selectedSentence.id);
    const selectedTeacher = React.useRef(teacher);
    const playbackRefreshId = React.useRef<number | null>(null);
    selectedSentenceId.current = selectedSentence.id;
    selectedTeacher.current = teacher;
    const selectedReady = !playbackSettingsRefreshing && selectedVoiceIsPlayable(selectedSentence, teacher);
    const selectedAudioDelayed = !selectedReady && selectedSentence.audio_request_status?.[teacher]?.state === 'retrying';
    const selectedAudioFailed = Boolean(selectedSentence.processing_error) &&
        !selectedSentence.audio_request_status?.[teacher];

    const refreshSelectedPlayback = React.useCallback((selectedVoice = selectedTeacher.current, clearError = true): Promise<void> => {
        const id = selectedSentenceId.current;
        if (id <= 0) return Promise.resolve();
        const version = ++playbackRefreshVersion.current;
        playbackRefreshId.current = id;
        setPlaybackSettingsRefreshing(true);
        if (clearError) setAudioRequestError(null);
        return CustomSentenceApi.getBySentenceId(id)
            .then((response) => {
                if (selectedSentenceId.current !== id || selectedTeacher.current !== selectedVoice ||
                    playbackRefreshVersion.current !== version) return;
                setSelectedSentence(response.data);
                playbackRefreshId.current = null;
                setPlaybackSettingsRefreshing(false);
                setAudioRequestError(null);
            })
            .catch((err) => {
                if (selectedSentenceId.current === id && selectedTeacher.current === selectedVoice &&
                    playbackRefreshVersion.current === version) setAudioRequestError(getInlineErrorMessage(err));
            });
    }, [setSelectedSentence]);
    // const [addSentenceTextField, setAddSentenceTextField] = React.useState("");

    const {
        text: addSentenceText,
        pinyin: addSentencePinyin,
        pinyinErrorText: addSentencePinyinErrorText,
        setDialogPromptFields: setAddSentenceFields,
        clearDialogPromptFields: clearAddSentenceFields,
        handleTextChange: handleAddSentenceChange,
        handlePinyinChange: handleAddSentencePinyinChange,
    } = useSentenceChinesePinyin({ history, location, setErrorMessage });

    React.useEffect(() => {
        if (addSentenceText && addSentenceText.trim().length > 0) {
            // Automatically generate pinyin whenever there is text
            handleAddSentenceChange({ target: { value: addSentenceText } } as React.ChangeEvent<HTMLInputElement>);
        } else {
            // Clear pinyin if sentence is empty
            handleAddSentenceChange({ target: { value: "" } } as React.ChangeEvent<HTMLInputElement>);
        }
    }, [addSentenceText, handleAddSentenceChange]);

    const queryParams = new URLSearchParams(location.search);
    const queryParamSentenceText = queryParams.get("text");
    const queryParamSentencePinyin = queryParams.get("pinyin");

    const [showPinyin, setShowPinyin] = React.useState(false);

    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

    const textFieldRef = React.useRef<HTMLInputElement | null>(null);

    const MAX_CHARACTER = 70;

    // const handleTextFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     const newValue = e.target.value;
    //     if (newValue.length > MAX_CHARACTER && textFieldRef.current) {
    //         setAnchorEl(textFieldRef.current); // Show popover if exceeding limit
    //     }
    //     setAddSentenceTextField(newValue.slice(0, MAX_CHARACTER));
    // };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    React.useEffect(() => {
        if (queryParamSentenceText) {
            const selectedSentence = { id: -1, text: "AddSentence", pinyin: [] };
            setSelectedSentence(selectedSentence);
            // setAddSentenceTextField(queryParamSentenceText);

            // const trimmedText =
            // queryParamSentenceText.length > MAX_CHARACTER
            //     ? queryParamSentenceText.slice(0, MAX_CHARACTER)
            //     : queryParamSentenceText;

            const queyrParamPinyinList = queryParamSentencePinyin ? queryParamSentencePinyin.trim().split(/\s+/).filter(Boolean) : null;
            
            // setAddSentenceFields(trimmedText, queyrParamPinyinList, null)
            setAddSentenceFields(queryParamSentenceText, queyrParamPinyinList, null)
        }
    }, [queryParamSentenceText, queryParamSentencePinyin, setAddSentenceFields, setSelectedSentence]);

    React.useEffect(() => {
        return AudioApi.subscribeCustomSentenceTeacher(() => {
            const nextTeacher = AudioApi.selectedCustomSentenceTeacher();
            requestedAudio.current.clear();
            requestAfterSettingsChange.current = selectedSentenceId.current > 0 ? `${selectedSentenceId.current}:${nextTeacher}` : null;
            selectedTeacher.current = nextTeacher;
            setTeacher(nextTeacher);
            refreshSelectedPlayback(nextTeacher);
        });
    }, [refreshSelectedPlayback]);

    React.useEffect(() => {
        setAudioRequestError(null);
    }, [selectedSentence.id, teacher]);

    React.useEffect(() => {
        if (!playbackSettingsRefreshing || !audioRequestError) return;
        // Keep playback gated until a status read succeeds, but recover from a
        // transient GET failure without requiring the student to press Retry.
        let inFlight = false;
        const timer = setInterval(() => {
            if (inFlight) return;
            inFlight = true;
            void refreshSelectedPlayback(selectedTeacher.current, false).finally(() => { inFlight = false; });
        }, 5000);
        return () => clearInterval(timer);
    }, [audioRequestError, playbackSettingsRefreshing, refreshSelectedPlayback]);

    React.useEffect(() => {
        if (playbackRefreshId.current !== null && playbackRefreshId.current !== selectedSentence.id) {
            playbackRefreshId.current = null;
            setPlaybackSettingsRefreshing(false);
        }
    }, [selectedSentence.id]);

    React.useEffect(() => {
        let interval: ReturnType<typeof setInterval> | null = null;

        if (selectedSentence && selectedSentence.id > 0 && !selectedReady && !playbackSettingsRefreshing && !selectedAudioFailed) {
            const sentenceId = selectedSentence.id;
            const selectedVoice = teacher;
            const playbackVersion = playbackRefreshVersion.current;
            let stopped = false;
            // Check the selected teacher voice, including slow playback requests
            // that can remain pending after the sentence queue has cleared.
            interval = setInterval(() => {
                CustomSentenceApi.getBySentenceId(sentenceId)
                    .then((response) => {
                        if (!stopped && selectedSentenceId.current === sentenceId && selectedTeacher.current === selectedVoice &&
                            playbackRefreshVersion.current === playbackVersion && response.status < 300) {
                            const updated = response.data;

                            // Update the selected sentence with the latest data from the API
                            setSelectedSentence(updated);

                            if (selectedVoiceIsPlayable(updated, selectedVoice) ||
                                (updated.processing_error && !updated.audio_request_status?.[selectedVoice])) {
                                if (interval) clearInterval(interval);
                                refreshCustomSentenceList();
                            }
                        }
                    })
                    .catch((err) => {
                        if (!stopped) console.error("Error refreshing processing queue", err);
                    });
            }, selectedAudioDelayed ? 30000 : 3000);

            return () => {
                stopped = true;
                if (interval) clearInterval(interval);
            };
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [selectedSentence, selectedReady, selectedAudioDelayed, selectedAudioFailed, teacher,
        playbackSettingsRefreshing, refreshCustomSentenceList, setSelectedSentence]);

    React.useEffect(() => {
        if (!selectedSentence || selectedSentence.id <= 0 || selectedReady || playbackSettingsRefreshing) return;
        const key = `${selectedSentence.id}:${teacher}`;
        const retry = explicitAudioRetries.current.has(key);
        const changedSettings = requestAfterSettingsChange.current === key;
        if (!retry && !changedSettings && (selectedSentence.processing_error || selectedSentence.audio_request_status?.[teacher])) return;
        if (requestedAudio.current.has(key) || audioRequestsInFlight.current.has(key)) return;
        explicitAudioRetries.current.delete(key);
        if (changedSettings) requestAfterSettingsChange.current = null;
        requestedAudio.current.add(key);
        audioRequestsInFlight.current.add(key);
        void CustomSentenceApi.requestAudio(selectedSentence.id, teacher, retry)
            .then(() => {
                if (selectedSentenceId.current === selectedSentence.id && selectedTeacher.current === teacher) {
                    setAudioRequestError(null);
                    refreshSelectedPlayback();
                }
            })
            .catch((err) => {
                requestedAudio.current.delete(key);
                if (selectedSentenceId.current === selectedSentence.id && selectedTeacher.current === teacher) {
                    setAudioRequestError(getInlineErrorMessage(err));
                }
            })
            .finally(() => {
                audioRequestsInFlight.current.delete(key);
                if (requestAfterSettingsChange.current === key) setAudioRequestRetry(current => current + 1);
            });
    }, [selectedSentence.id, selectedSentence.processing_error, selectedSentence.audio_request_status?.[teacher]?.state,
        selectedReady, teacher, audioRequestRetry, playbackSettingsRefreshing, refreshSelectedPlayback]);

    const retrySelectedAudio = () => {
        if (playbackSettingsRefreshing) {
            refreshSelectedPlayback();
            return;
        }
        const key = `${selectedSentence.id}:${teacher}`;
        if (audioRequestsInFlight.current.has(key)) return;
        explicitAudioRetries.current.add(key);
        requestedAudio.current.delete(key);
        setAudioRequestError(null);
        setAudioRequestRetry((current) => current + 1);
    };

    React.useEffect(() => {
        const publicSentenceScoreId = localStorage.getItem(LS_PUBLIC_SCORE_IMPORT_ID);
        localStorage.removeItem(LS_PUBLIC_SCORE_IMPORT_ID);
        if (publicSentenceScoreId) {
            CustomSentenceApi.createPublic(publicSentenceScoreId)
                .then((response) => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    throw new Error("Unable to create sentence statuscode=" + response.status + " " + response.statusText);
                })
                .then((created) => {
                    if (created.id > 0) requestedAudio.current.add(`${created.id}:${teacher}`);
                    refreshCustomSentenceList();
                })
                .catch((err) => {
                    if (err.response.status == 490) {
                        setAddErrorMessage("Please validate your email before adding sentences. You can validate your email in your profile.");
                    } else if (err.response.status == 491) {
                        setAddErrorMessage(getInlineErrorMessage(err));
                    } else if (err.response.status == 492) {
                        setAddErrorMessage("This user is read only. If you would like to add sentences, please logout and register a new user.");
                    } else {
                        console.error(err);
                        handleErrorMessage(err, setErrorMessage, history, location);
                    }
                })
                .finally(() => {
                    setPublicSentenceScoreDone(true);
                });
        } else {
            setPublicSentenceScoreDone(true);
        }
    }, [history, location, refreshCustomSentenceList, setErrorMessage]);

    React.useEffect(() => {
        if (publicSentenceScoreDone) {
            CustomSentenceApi.getAll()
                .then((response) => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    console.error(response);
                    handleErrorMessage(response, setErrorMessage, history, location);
                    return [];
                })
                .then((list) => setCustomSentenceList(list))
                .catch((err) => {
                    handleErrorMessage(err, setErrorMessage, history, location);
                });
        }
    }, [publicSentenceScoreDone, history, location, setCustomSentenceList, setErrorMessage]);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setSubmitting(true);
        const data = new FormData(event.currentTarget);

        let sentence = data.get("sentence");
        if (sentence == null) {
            sentence = "";
        } else {
            sentence = sentence.toString().trim();
        }

        let pinyin = data.get("pinyin");
        if (pinyin == null) {
            pinyin = "";
        } else {
            pinyin = pinyin.toString().trim();
        }
        const pinyinList = pinyin.trim().split(/\s+/).filter(Boolean)

        if (sentence.length === 0) {
            setAddErrorMessage("Sentence is blank");
            setSubmitting(false);
        } else {
            setAddErrorMessage(null);
            CustomSentenceApi.create({ id: 0, text: sentence, pinyin: pinyinList,
                teacher_voice: teacher })
                .then((response) => {
                    if (response.status < 300) {
                        return response.data;
                    }
                    throw new Error("Unable to create sentence statuscode=" + response.status + " " + response.statusText);
                })
                .then(() => {
                    refreshCustomSentenceList();
                    clearAddSentenceFields();
                    // setAddSentenceTextField("");

                    // navigate back after success
                    history.push("/sentence");
                })
                .catch((err) => {
                    if (err.response && err.response.status == 490) {
                        setAddErrorMessage("Please validate your email before adding sentences. You can validate your email in your profile.");
                    } else if (err.response && err.response.status == 491) {
                        setAddErrorMessage(getInlineErrorMessage(err));
                    } else if (err.response && err.response.status == 492) {
                        setAddErrorMessage("This user is read only. If you would like to add sentences, please logout and register a new user.");
                    } else {
                        console.error(err);
                        handleErrorMessage(err, setErrorMessage, history, location);
                    }
                })
                .finally(() => {
                    setSubmitting(false);
                });
        }
    };

    const handlePlayAudio = (sentenceId: number) => {
        const audioPromise = AudioApi.getTeacherSentence(sentenceId)

        audioPromise
            .then(response => {
                // check if the response was successful
                if (response.status >= 300) {
                    throw new Error('Getting male audio was not ok statuscode=' + response.status + " " + response.statusText);
                }
                return response.data;
            })
            .then(blob => {
                playAudio(blob)
                // create a new Audio object and set the source to the blob URL
                // const audio = new Audio(URL.createObjectURL(blob));
                // audio.play();
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    };

    return (
        <section style={redesigned ? { height: '100%', overflowY: 'auto' } : undefined}>
            {(!redesigned || isMobile || selectedSentence.id > 0 || selectedSentence.text === 'AddSentence') && <Container
                maxWidth={false} // Take full width of the viewport
                sx={{
                    height: "auto",
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    pt: redesigned ? { xs: 2, md: 3 } : 2,
                    backgroundColor: redesigned ? '#fff' : "#F2F7FF",
                }}
                >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 1,
                    }}
                >
                    {(!redesigned || isMobile) && <Button
                    onClick={onToggleMenuCollapse}
                    aria-label={redesigned ? 'Back to prior sentences' : isMenuCollapsed ? 'Expand menu' : 'Collapse menu'}
                    sx={{
                        minWidth: "40px",
                        height: "40px",
                        borderRadius: "4px",
                        backgroundColor: "transparent",
                        "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
                    }}
                    >
                    {redesigned ? <ArrowBackRoundedIcon /> : <img
                        src={isMenuCollapsed ? SidebarExpandIcon : SidebarCollapseIcon}
                        style={{ width: "24px", height: "24px" }}
                        alt={isMenuCollapsed ? "Expand Menu" : "Collapse Menu"}
                    />}
                    </Button>}
                    {redesigned ? <Typography component="h2" sx={{ color: SLATE, fontSize: 11, fontWeight: 700, letterSpacing: '.08em', ml: isMobile ? 2 : 0 }}>
                        {selectedSentence.text === 'AddSentence' ? 'ADD A SENTENCE' : selectedSentence.id > 0 ? 'PRONUNCIATION PRACTICE' : 'PRACTICE'}
                    </Typography> : <Typography variant="h2" sx={{ color: "#002A6E", ml: 2 }}>Check Pronunciation</Typography>}
                </Box>
            </Container>}

            {selectedSentence.id == -1 && selectedSentence.text == "AddSentence" ? (
                <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                    sx={{
                        mt: redesigned ? 0 : 2,
                        mx: redesigned ? 0 : 3,
                        borderRadius: redesigned ? '16px' : 4,
                        height: redesigned ? '100%' : "calc(100vh - 300px)",
                        backgroundColor: "white",
                        overflow: "hidden", // Hide the scrollbar if content doesn't overflow
                        display: "flex", // Use flexbox
                        alignItems: "center", // Center vertically
                        flexDirection: "column",
                        justifyContent: "center", // Center horizontally
                        gap: 3,
                    }}>
                    <SendButtonTextField
                        label="Sentence"
                        name="sentence"
                        value={addSentenceText}
                        disabled={isSubmitting}
                        // onChange={(e) => setAddSentenceTextField(e.target.value)}
                        onChange={handleAddSentenceChange}
                        ref={textFieldRef}
                        onSubmit={() => { } /* handleSubmit logic here if needed, or handle it through form submission */}
                        onKeyDown={() => { } /* handleSubmit logic here if needed, or handle it through form submission */}
                        required
                        sx={{ width: "80%" }} // Directly control the width here
                    />

                    <Tooltip title="Pinyin is automatically generated based on the sentence" arrow>
                        <TextField
                            label="Pinyin"
                            name="pinyin"
                            className="border-light"
                            value={addSentencePinyin}
                            onChange={handleAddSentencePinyinChange}
                            required
                            error={addSentencePinyinErrorText != null && addSentencePinyinErrorText.length > 0}
                            helperText={addSentencePinyinErrorText}
                            sx={{ width: "80%", pt: 2, }}
                            InputProps={{
                                readOnly: true,
                                sx: {
                                    height: 40, // input container height
                                    display: 'flex',
                                    alignItems: 'center', // center the text vertically
                                    pointerEvents: 'none', // user cannot click/focus
                                },
                            }}
                            InputLabelProps={{
                                sx: { top: 0, lineHeight: '40px', },
                            }}
                        />
                    </Tooltip>

                    <Typography variant="h6" sx={{ color: redesigned ? SLATE : "#002A6E", opacity: redesigned ? 1 : 0.3, pb: 4 }}>
                        Your selected voice will be prepared first. You can practice as soon as it is ready.
                    </Typography>

                    {isSubmitting && <CircularProgress size={48} sx={{ ml: 2, mt: 4 }} />}
                    {addErrorMessage && (
                        <Typography
                            color="error"
                            style={{
                                display: "flex",
                                justifyContent: "center",
                                marginTop: "1em",
                            }}>
                            {addErrorMessage}
                        </Typography>
                    )}
                    <Popover
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handlePopoverClose}
                        anchorOrigin={{
                            vertical: "top",
                            horizontal: "center",
                        }}
                        transformOrigin={{
                            vertical: "bottom",
                            horizontal: "center",
                        }}
                        disableRestoreFocus
                    >
                        <Typography sx={{ p: 1, fontSize: 12, color: "red" }}>
                            Max {MAX_CHARACTER} characters allowed.
                        </Typography>
                    </Popover>
                </Box>
            ) : !selectedReady ? (
                <Box
                    sx={{
                        mt: redesigned ? 0 : 2,
                        mx: redesigned ? 0 : 3,
                        borderRadius: redesigned ? '16px' : 4,
                        height: redesigned ? '100%' : "calc(100vh - 300px)",
                        backgroundColor: "white",
                        overflow: "hidden", // Hide the scrollbar if content doesn't overflow
                        display: "flex", // Use flexbox
                        alignItems: "center", // Center vertically
                        justifyContent: "center", // Center horizontally
                    }}>
                    <Stack
                        spacing={2}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography variant="h3" sx={{ color: redesigned ? NAVY : "#002A6E", opacity: redesigned ? 1 : 0.3, pl: 2 }}>
                            {selectedSentence.text}
                        </Typography>
                        <Typography variant="h4" sx={{ color: redesigned ? SLATE : "#002A6E", opacity: redesigned ? 1 : 0.3, pl: 2 }}>
                            {audioRequestError
                                ? `${playbackSettingsRefreshing ? 'Unable to check audio status' : 'Unable to request audio'}: ${audioRequestError}`
                                : playbackSettingsRefreshing
                                ? 'Checking audio for your playback settings.'
                                : selectedAudioFailed
                                ? 'Audio preparation failed. You can retry now. If it fails again, contact your teacher or support.'
                                : selectedAudioDelayed
                                ? 'Audio generation is delayed. The queue will retry automatically, or you can retry now.'
                                : 'Please wait. Your selected teacher voice is being prepared.'}
                            <br />
                        </Typography>
                        {!selectedAudioDelayed && (selectedSentence.processing_queue ?? 0) > 0 && <Typography variant="h5" sx={{ color: redesigned ? NAVY : "#002A6E", opacity: 0.9 }}>
                            Position: {selectedSentence.processing_queue}
                        </Typography>}
                        {(audioRequestError || (!playbackSettingsRefreshing && (selectedAudioFailed || selectedAudioDelayed))) &&
                            <Button onClick={retrySelectedAudio}>Retry audio</Button>}
                    </Stack>
                </Box>
            ) : selectedSentence.text.length <= 0 ? (
                <Box
                    sx={{
                        mt: redesigned ? 0 : 2,
                        mx: redesigned ? 0 : 3,
                        borderRadius: redesigned ? '16px' : 4,
                        height: redesigned ? (isMobile ? 'calc(100% - 70px)' : '100%') : "calc(100vh - 300px)",
                        backgroundColor: "white",
                        overflow: "hidden", // Hide the scrollbar if content doesn't overflow
                        display: "flex", // Use flexbox
                        alignItems: "center", // Center vertically
                        justifyContent: "center", // Center horizontally
                    }}>
                    <Stack
                        spacing={2}
                        alignItems="center"
                        justifyContent="center"
                        sx={{
                            width: "100%",
                            height: "100%",
                            p: 4,
                        }}>
                        {redesigned && <Box sx={{ width: 64, height: 64, borderRadius: '18px', display: 'grid', placeItems: 'center', color: BLUE, backgroundColor: SOFT_BLUE }}><AddRoundedIcon sx={{ fontSize: 30 }} /></Box>}
                        <Typography variant={redesigned ? 'h5' : isMenuCollapsed ? "h3" : "h2"} sx={{ color: redesigned ? NAVY : "#002A6E", opacity: redesigned ? 1 : 0.3 }}>
                            {redesigned ? customSentenceList?.length ? 'Select a sentence to practice' : 'No practice sentences yet' : 'Please select a sentence'}
                        </Typography>
                        {redesigned && <Typography sx={{ maxWidth: 420, textAlign: 'center', fontSize: 13.5, color: SLATE }}>{customSentenceList?.length ? 'Choose a sentence from the list to listen, record, and review your pronunciation.' : 'Add a Chinese sentence to generate pinyin, model audio, and recording feedback.'}</Typography>}
                        {redesigned && !customSentenceList?.length && <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setSelectedSentence({ id: -1, text: 'AddSentence', pinyin: [] })} sx={{ backgroundColor: YELLOW, color: INK, '&:hover': { backgroundColor: YELLOW_HOVER } }}>Add a sentence</Button>}
                    </Stack>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        height: "auto",
                        pl: 2,
                        pr: 2,
                        pb: 4,
                        mt: 2,
                        gap: redesigned ? 0 : 2, // The redesign uses one practice surface.
                    }}>
                    <Box
                        sx={{
                            display: "flex",
                            flex: 1, // Take up half of the container
                            gap: redesigned ? 0 : 2,
                            flexDirection: redesigned ? 'column' : { xs: "column", sm: "row" },
                        }}>
                        {/* Left Panel */}
                        <Box
                            sx={{
                                flex: 1, // Take up half of the top-half space
                                display: "flex",
                                flexDirection: "column", // Stack content vertically
                                justifyContent: "center", // Vertically center content
                                alignItems: "center", // Horizontally center content
                                backgroundColor: "white",
                                borderTopLeftRadius: 16, // Round top-left corner with a radius of 16px
                                borderTopRightRadius: { xs: 16, sm: 0 },
                                py: 2,
                                px: 3,
                            }}>
                            <Typography variant="h4" sx={{ minHeight: "40px" }}>
                                Listen to the sentence
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", rowGap: 0, columnGap: 1 }}>
                                {(() => {
                                    let pinyinIndex = 0; // separate pointer for Chinese chars

                                    return selectedSentence.text.split("").map((char, index) => {
                                        const isChinese = /[\u4e00-\u9fff]/.test(char);
                                        let pinyinForChar: string | null = null;

                                        if (isChinese) {
                                            // take pinyin only for Chinese chars
                                            pinyinForChar =
                                                selectedSentence.pinyin && selectedSentence.pinyin[pinyinIndex]
                                                    ? selectedSentence.pinyin[pinyinIndex]
                                                    : null;
                                            pinyinIndex++; // move forward only when char is Chinese
                                        }

                                        return (
                                            <Stack key={index} alignItems="center" sx={{ minWidth: isChinese ? "24px" : "auto" }}>
                                                <Typography className={isChinese ? kaiTiFontClass.customFont : undefined} sx={{ fontSize: isChinese ? "28px" : "inherit" }}>
                                                    {char}
                                                </Typography>

                                                {(alwaysShowPinyin || showPinyin) && isChinese && pinyinForChar && (
                                                    <Typography variant="body2" sx={{ fontSize: "16px", color: redesigned ? SLATE : "#888", fontWeight: redesigned ? 600 : 500 }}>
                                                        {pinyinForChar}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        );
                                    });
                                })()}
                            </Box>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                {!alwaysShowPinyin && <FormControlLabel control={<Switch checked={showPinyin} onChange={(e) => setShowPinyin(e.target.checked)} color="primary" />} label="Pinyin" />}
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <TeacherAudioActions
                                        context={{
                                            sentenceId: selectedSentence.id,
                                            textToSpeechSpeakerId: getSelectedTeacherSpeakerRole(),
                                            audioText: selectedSentence.text,
                                            audioRole: 'pronunciation_sentence_playback',
                                        }}
                                        onPlay={() => handlePlayAudio(selectedSentence.id)}
                                        speakerSize={24}
                                    />
                                    <Typography>Play Audio</Typography>
                                </Stack>
                            </Stack>
                        </Box>
                        {/* Right Panel */}
                        <Box
                            sx={{
                                flex: 1, // Take up half of the top-half space
                                display: "flex",
                                flexDirection: "column", // Stack content vertically
                                justifyContent: "center", // Vertically center content
                                alignItems: "center", // Horizontally center content
                                backgroundColor: "white",
                                borderTopRightRadius: { xs: 0, sm: 16 }, // Round top-left corner with a radius of 16px
                                py: 2,
                                px: 3,
                            }}>
                            <Typography variant="h4" sx={{ minHeight: "40px" }}>
                                Record yourself
                            </Typography>
                            <Typography align="center" sx={{ minHeight: "75px", lineHeight: "2", maxWidth: "80%" }} variant="h6">
                                For accurate results, please use a quiet room and review the recording for any cutoff words.
                            </Typography>
                            <CustomSentenceRecorderControl recorderState={recorderState} handlers={handlers} isMobile={isMobile} redesigned={redesigned} />
                        </Box>
                    </Box>
                    {/* Bottom Panel */}
                    <Box
                        sx={{
                            flex: 1, // Take up the remaining half of the container
                            backgroundColor: "white",
                            borderBottomLeftRadius: 16, // Round top-left corner with a radius of 16px
                            borderBottomRightRadius: 16, // Round top-left corner with a radius of 16px
                            pb: 2
                        }}>
                        <CustomSentenceRecordingList audio={audio} selectedSentence={selectedSentence} isMobile={isMobile} redesigned={redesigned} />
                    </Box>
                </Box>
            )}
        </section>
    );
}
